import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatJamaicaDate } from "@/lib/datetime";
import { formatDistanceToNow } from "date-fns";
import {
  CreditCard, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Pause, Play, X, Plus, Tag,
  MoreHorizontal, ExternalLink, ChevronDown, ChevronRight, ChevronLeft, Search, Loader2,
  Apple, Hand, Users, CalendarClock, TrendingUp, Receipt, RotateCcw, FlaskConical, Mail, MessageCircle, Phone,
  BarChart3,
} from "lucide-react";

// ── Types (mirror server/services/adminSubscriptions.ts) ────────────────────

type View = "active" | "ending" | "past_due" | "paused" | "trial" | "cancelled" | "expired" | "apple" | "attention" | "all";

interface Summary {
  active: number; activeMonthly: number; activeYearly: number;
  activeStripe: number; activeApple: number; activeManual: number;
  endingAtPeriodEnd: number; renewing7d: number; ending7d: number;
  pastDue: number; paused: number; trial: number; cancelled: number; expired: number;
  attention: { activeLapsed: number; activeNoEndDate: number; pastDueStale: number; total: number };
  byGrade: Array<{ grade: number | null; count: number }>;
  estimatedMrrJmd: number; estimatedMrrAppleUsd: number;
  last30d: { newSubscriptions: number; cancellations: number; paymentsSucceeded: number; paymentsFailed: number; paymentsRecovered: number; revenue: number; revenueCurrency: string };
  generatedAt: string;
}

interface Row {
  id: string; firstName: string; lastName: string; email: string | null; username: string | null;
  grade: number | null; subscribedGrade: number | null; parentEmail: string | null; parentPhone: string | null;
  parentName: string | null; parentWhatsappVerified: boolean; school: string | null;
  parentAccount: { id: string; name: string; email: string | null } | null;
  portalParents: Array<{ id: string; name: string; email: string | null; phone: string | null; relationship: string; isPrimary: boolean; lastLoginAt: string | null }>;
  isTestAccount: boolean;
  subscriptionStatus: string | null; subscriptionPlan: string | null; subscriptionEndDate: string | null;
  subscriptionPausedUntil: string | null; subscriptionCancelAtPeriodEnd: boolean;
  provider: "stripe" | "apple" | "manual"; stripeCustomerId: string | null; stripeSubscriptionId: string | null;
  paymentFailedAt: string | null; dunningLastDay: number; createdAt: string; lastActiveAt: string | null; firstPaidAt: string | null;
  lastPayment: { at: string; type: "payment_succeeded" | "payment_failed"; amount: number; currency: string; failureReason: string | null; attemptCount: number } | null;
  flags: string[];
}

interface ListResponse { rows: Row[]; total: number; page: number; pageSize: number; totalPages: number }

interface SyncResult {
  userId: string; found: boolean; message?: string;
  stripe?: { subscriptionId: string; status: string; cancelAtPeriodEnd: boolean; periodEnd: string | null; priceAmount: number | null; currency: string | null; interval: string | null };
  changes: Array<{ field: string; from: string | null; to: string | null }>;
}

interface ReconcileResult {
  checked: number;
  changed: Array<{ userId: string; name: string; email: string | null; changes: SyncResult["changes"] }>;
  notFound: Array<{ userId: string; name: string; email: string | null; message: string }>;
  errors: Array<{ userId: string; name: string; email: string | null; error: string }>;
  durationMs: number;
}

interface RetryResult { ok: boolean; invoiceId?: string; amount?: number; currency?: string; invoiceStatus?: string; hostedInvoiceUrl?: string | null; reason?: string; code?: string }

// ── Helpers ─────────────────────────────────────────────────────────────────

const VIEWS: Array<{ value: View; label: string }> = [
  { value: "active", label: "Active" },
  { value: "ending", label: "Ending" },
  { value: "past_due", label: "Past due" },
  { value: "paused", label: "Paused" },
  { value: "trial", label: "Trial" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
  { value: "apple", label: "Apple" },
  { value: "attention", label: "Needs attention" },
  { value: "all", label: "All" },
];

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const t = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return debounced;
}

function money(minor: number, currency: string): string {
  const amount = minor / 100;
  const cur = (currency || "JMD").toUpperCase();
  const prefix = cur === "JMD" ? "J$" : cur === "USD" ? "US$" : `${cur} `;
  return `${prefix}${amount.toLocaleString("en-JM", { maximumFractionDigits: amount % 1 === 0 ? 0 : 2 })}`;
}

function jmd(amount: number): string {
  return `J$${Math.round(amount).toLocaleString("en-JM")}`;
}

function relative(iso: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return formatDistanceToNow(d, { addSuffix: true });
}

function StatusBadge({ status }: { status: string | null }) {
  switch (status) {
    case "active": return <Badge className="bg-green-600 hover:bg-green-600">Active</Badge>;
    case "trial": return <Badge className="bg-blue-600 hover:bg-blue-600">Trial</Badge>;
    case "past_due": return <Badge className="bg-orange-600 hover:bg-orange-600">Past due</Badge>;
    case "paused": return <Badge className="bg-yellow-600 hover:bg-yellow-600">Paused</Badge>;
    case "canceled":
    case "cancelled": return <Badge variant="destructive">Cancelled</Badge>;
    case "expired": return <Badge variant="secondary">Expired</Badge>;
    case "inactive": return <Badge variant="secondary">Inactive</Badge>;
    default: return <Badge variant="outline">None</Badge>;
  }
}

function ProviderBadge({ row }: { row: Row }) {
  if (row.provider === "apple") {
    return <Badge variant="outline" className="gap-1"><Apple className="w-3 h-3" />Apple</Badge>;
  }
  if (row.provider === "stripe") {
    return (
      <a
        href={`https://dashboard.stripe.com/subscriptions/${row.stripeSubscriptionId}`}
        target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        data-testid={`link-stripe-${row.id}`}
      >
        <CreditCard className="w-3 h-3" />Stripe<ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return <Badge variant="outline" className="gap-1"><Hand className="w-3 h-3" />Manual</Badge>;
}

const FLAG_LABELS: Record<string, { label: string; tone: "warn" | "info" }> = {
  lapsed_end_date: { label: "Active but end date passed", tone: "warn" },
  no_end_date: { label: "Active with no end date", tone: "warn" },
  past_due_30d: { label: "Past due for 30+ days", tone: "warn" },
  cancels_at_period_end: { label: "Cancels at period end", tone: "info" },
  manual_access: { label: "No Stripe subscription (manual access)", tone: "info" },
};

/** Jamaican numbers come in as 876-xxx-xxxx, (876) xxx xxxx, +1876..., etc. wa.me wants digits with country code. */
function waNumber(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `1${digits}`;
  if (digits.length === 7) return `1876${digits}`;
  return digits;
}

function ParentLine({ row }: { row: Row }) {
  const hasContact = row.parentName || row.parentEmail || row.parentPhone;
  if (!hasContact && row.portalParents.length === 0) return <div className="text-xs text-muted-foreground/70">No parent on file</div>;
  const search = row.parentEmail || row.parentPhone || "";
  return (
    <div className="text-xs text-muted-foreground space-y-0.5 mt-0.5" data-testid={`parents-${row.id}`}>
      {hasContact && (
        <div className="flex flex-wrap items-center gap-x-1.5">
          <span>Parent:</span>
          <Link href={`/admin/parents?search=${encodeURIComponent(search)}`} className="text-primary hover:underline" title="Open on the Parents page">
            {row.parentName || row.parentEmail || row.parentPhone}
          </Link>
          {row.parentName && row.parentEmail && <a href={`mailto:${row.parentEmail}`} className="hover:underline">{row.parentEmail}</a>}
          {row.parentPhone && (
            <a href={`https://wa.me/${waNumber(row.parentPhone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline" title={row.parentWhatsappVerified ? "WhatsApp verified" : "Open in WhatsApp"}>
              <Phone className="w-3 h-3" />{row.parentPhone}{row.parentWhatsappVerified && <span className="text-green-600">✓</span>}
            </a>
          )}
          {row.parentAccount && (
            <Link href={`/admin/users?openUser=${row.parentAccount.id}`} className="text-primary hover:underline" title="Parent has an EduFocal account">(account)</Link>
          )}
        </div>
      )}
      {row.portalParents.map(pp => (
        <div key={pp.id} className="flex flex-wrap items-center gap-x-1.5">
          <span>Portal{pp.relationship && pp.relationship !== "guardian" ? ` · ${pp.relationship}` : ""}:</span>
          <span className="text-foreground/80">{pp.name}</span>
          {pp.email && <a href={`mailto:${pp.email}`} className="hover:underline">{pp.email}</a>}
          {pp.phone && <a href={`https://wa.me/${waNumber(pp.phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:underline"><Phone className="w-3 h-3" />{pp.phone}</a>}
          {pp.lastLoginAt && <span className="text-muted-foreground/70">· last login {relative(pp.lastLoginAt)}</span>}
        </div>
      ))}
    </div>
  );
}

// ── Payment history panel (expanded row) ────────────────────────────────────

function PaymentHistoryPanel({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useQuery<{
    message?: string;
    upcomingPayments?: Array<{ amount: number; date: string; description: string; currency?: string }>;
    pastPayments?: Array<{ id: string; amount: number; date: string; status: string; description: string; currency?: string; invoiceUrl?: string | null; invoicePdf?: string | null }>;
  }>({ queryKey: [`/api/admin/users/${userId}/payment-history`] });

  if (isLoading) return <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 className="w-4 h-4 animate-spin" />Loading Stripe invoices…</div>;
  if (isError) return <p className="text-sm text-destructive py-3">Could not load invoices from Stripe.</p>;
  if (data?.message && !data.pastPayments?.length) return <p className="text-sm text-muted-foreground py-3">{data.message}</p>;

  return (
    <div className="grid gap-4 md:grid-cols-[2fr_1fr] py-3" data-testid={`panel-payments-${userId}`}>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Invoices</h4>
        {data?.pastPayments?.length ? (
          <table className="w-full text-sm">
            <tbody>
              {data.pastPayments.map(inv => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{formatJamaicaDate(inv.date)}</td>
                  <td className="py-1.5 pr-3">{inv.description}</td>
                  <td className="py-1.5 pr-3 text-right whitespace-nowrap">{money(Math.round(inv.amount * 100), inv.currency || "JMD")}</td>
                  <td className="py-1.5 pr-3">
                    {inv.status === "paid"
                      ? <Badge className="bg-green-600 hover:bg-green-600">paid</Badge>
                      : <Badge variant={inv.status === "open" ? "outline" : "secondary"}>{inv.status}</Badge>}
                  </td>
                  <td className="py-1.5 text-right">
                    {inv.invoiceUrl && <a href={inv.invoiceUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">View<ExternalLink className="w-3 h-3" /></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-muted-foreground">No invoices yet.</p>}
      </div>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Upcoming</h4>
        {data?.upcomingPayments?.length ? data.upcomingPayments.map((u, i) => (
          <div key={i} className="text-sm">
            <div className="font-medium">{money(Math.round(u.amount * 100), u.currency || "JMD")}</div>
            <div className="text-muted-foreground">{formatJamaicaDate(u.date)} · {u.description}</div>
          </div>
        )) : <p className="text-sm text-muted-foreground">Nothing scheduled.</p>}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function AdminSubscriptions() {
  const { toast } = useToast();

  const [view, setView] = useState<View>("active");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [plan, setPlan] = useState("all");
  const [grade, setGrade] = useState("all");
  const [provider, setProvider] = useState("all");
  const [includeTest, setIncludeTest] = useState(false);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDir, setSortDir] = useState<"asc" | "desc" | "">("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [extendTarget, setExtendTarget] = useState<Row | null>(null);
  const [extendDays, setExtendDays] = useState("30");
  const [couponTarget, setCouponTarget] = useState<Row | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [cancelTarget, setCancelTarget] = useState<Row | null>(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<ReconcileResult | null>(null);
  const [syncResult, setSyncResult] = useState<{ row: Row; result: SyncResult } | null>(null);
  const [retryResult, setRetryResult] = useState<{ row: Row; result: RetryResult } | null>(null);

  useEffect(() => { setPage(1); }, [view, search, plan, grade, provider, includeTest, sortBy, sortDir]);

  const listUrl = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set("view", view);
    if (search) qs.set("search", search);
    if (plan !== "all") qs.set("plan", plan);
    if (grade !== "all") qs.set("grade", grade);
    if (provider !== "all") qs.set("provider", provider);
    if (includeTest) qs.set("includeTest", "true");
    if (sortBy) qs.set("sortBy", sortBy);
    if (sortDir) qs.set("sortDir", sortDir);
    qs.set("page", String(page));
    qs.set("pageSize", "25");
    return `/api/admin/subscriptions?${qs.toString()}`;
  }, [view, search, plan, grade, provider, includeTest, sortBy, sortDir, page]);

  const { data: summary, isLoading: summaryLoading } = useQuery<Summary>({ queryKey: ["/api/admin/subscriptions/summary"] });
  const { data: list, isLoading: listLoading, isFetching, isError, error } = useQuery<ListResponse>({
    queryKey: [listUrl],
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ predicate: q => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/admin/subscriptions") });
    queryClient.invalidateQueries({ predicate: q => typeof q.queryKey[0] === "string" && /^\/api\/admin\/users\//.test(q.queryKey[0] as string) });
  };

  const fail = (title: string) => (e: any) => toast({ title, description: e?.message || "Request failed", variant: "destructive" });

  const extendMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) => apiRequest("POST", `/api/admin/users/${userId}/extend-subscription`, { days }),
    onSuccess: () => { invalidate(); toast({ title: "Subscription extended" }); setExtendTarget(null); },
    onError: fail("Could not extend"),
  });
  const pauseMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/pause-subscription`, {}),
    onSuccess: () => { invalidate(); toast({ title: "Subscription paused" }); },
    onError: fail("Could not pause"),
  });
  const resumeMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/resume-subscription`, {}),
    onSuccess: () => { invalidate(); toast({ title: "Subscription resumed" }); },
    onError: fail("Could not resume"),
  });
  const cancelMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/cancel-subscription`, {}),
    onSuccess: () => { invalidate(); toast({ title: "Subscription cancelled", description: "Stripe subscriptions end at the period end; access continues until then." }); setCancelTarget(null); },
    onError: fail("Could not cancel"),
  });
  const couponMutation = useMutation({
    mutationFn: async ({ userId, code }: { userId: string; code: string }) => (await apiRequest("POST", `/api/admin/users/${userId}/apply-coupon`, { code })).json(),
    onSuccess: (data: any) => { invalidate(); toast({ title: "Discount applied", description: data?.discountDescription }); setCouponTarget(null); setCouponCode(""); },
    onError: fail("Could not apply discount"),
  });
  const syncMutation = useMutation({
    mutationFn: async (row: Row) => ({ row, result: (await (await apiRequest("POST", `/api/admin/subscriptions/${row.id}/sync`, {})).json()) as SyncResult }),
    onSuccess: (data) => {
      invalidate();
      if (!data.result.found) toast({ title: "Nothing to sync", description: data.result.message });
      else if (data.result.changes.length === 0) toast({ title: "Already in sync with Stripe", description: `Stripe status: ${data.result.stripe?.status}` });
      else setSyncResult(data);
    },
    onError: fail("Sync failed"),
  });
  const retryMutation = useMutation({
    mutationFn: async (row: Row) => ({ row, result: (await (await apiRequest("POST", `/api/admin/subscriptions/${row.id}/retry-payment`, {})).json()) as RetryResult }),
    onSuccess: (data) => { invalidate(); setRetryResult(data); },
    onError: fail("Retry failed"),
  });
  const reconcileMutation = useMutation({
    mutationFn: async () => (await (await apiRequest("POST", `/api/admin/subscriptions/reconcile`, {})).json()) as ReconcileResult,
    onSuccess: (data) => { invalidate(); setReconcileResult(data); },
    onError: fail("Reconcile failed"),
  });

  const toggleExpanded = (id: string) => setExpanded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const sortToggle = (field: string) => {
    if (sortBy === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("asc"); }
  };
  const go = (v: View) => { setView(v); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const s = summary;
  const net30 = s ? s.last30d.newSubscriptions - s.last30d.cancellations : 0;

  return (
    <div className="space-y-6" data-testid="page-admin-subscriptions">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscriptions</h1>
          <p className="text-muted-foreground">Active, ending, past-due, paused and cancelled subscriptions across Stripe, Apple and manual access.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link href="/admin/revenue"><Button variant="outline" size="sm"><TrendingUp className="w-4 h-4 mr-1" />Revenue</Button></Link>
          <Link href="/admin/payment-analytics"><Button variant="outline" size="sm">Payment analytics</Button></Link>
          <Link href="/admin/cancellation-feedback"><Button variant="outline" size="sm">Cancellation feedback</Button></Link>
          <Link href="/admin/stripe-pricing"><Button variant="outline" size="sm">Pricing</Button></Link>
          <Button size="sm" onClick={() => setReconcileOpen(true)} disabled={reconcileMutation.isPending} data-testid="button-reconcile">
            {reconcileMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Reconcile with Stripe
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <KpiCard
          title="Active" icon={<Users className="w-4 h-4" />} loading={summaryLoading}
          value={s?.active} onClick={() => go("active")} active={view === "active"}
          sub={s ? `${s.activeMonthly} monthly · ${s.activeYearly} yearly` : ""}
          foot={s ? `Est. MRR ${jmd(s.estimatedMrrJmd)}${s.activeApple ? ` + US$${s.estimatedMrrAppleUsd} Apple` : ""}` : ""}
          testId="kpi-active"
        />
        <KpiCard
          title="Renewing in 7 days" icon={<CalendarClock className="w-4 h-4" />} loading={summaryLoading}
          value={s?.renewing7d} onClick={() => { setSortBy("endDate"); setSortDir("asc"); go("active"); }}
          sub={s ? `${s.ending7d} ending this week` : ""}
          testId="kpi-renewing"
        />
        <KpiCard
          title="Ending at period end" icon={<X className="w-4 h-4" />} loading={summaryLoading}
          value={s?.endingAtPeriodEnd} onClick={() => go("ending")} active={view === "ending"}
          sub="Cancelled, access still on"
          testId="kpi-ending"
        />
        <KpiCard
          title="Past due" icon={<AlertTriangle className="w-4 h-4" />} loading={summaryLoading}
          value={s?.pastDue} onClick={() => go("past_due")} active={view === "past_due"} tone={s && s.pastDue > 0 ? "warn" : undefined}
          sub={s ? `${s.last30d.paymentsFailed} failed · ${s.last30d.paymentsRecovered} recovered (30d)` : ""}
          testId="kpi-past-due"
        />
        <KpiCard
          title="Paused" icon={<Pause className="w-4 h-4" />} loading={summaryLoading}
          value={s?.paused} onClick={() => go("paused")} active={view === "paused"}
          sub={s && s.trial ? `${s.trial} on trial` : " "}
          testId="kpi-paused"
        />
        <KpiCard
          title="Last 30 days" icon={<Receipt className="w-4 h-4" />} loading={summaryLoading}
          value={s ? (net30 >= 0 ? `+${net30}` : `${net30}`) : undefined} onClick={() => go("cancelled")} active={view === "cancelled"}
          sub={s ? `${s.last30d.newSubscriptions} new · ${s.last30d.cancellations} cancelled` : ""}
          foot={s ? `${money(s.last30d.revenue, s.last30d.revenueCurrency)} from ${s.last30d.paymentsSucceeded} payments` : ""}
          testId="kpi-30d"
        />
        <KpiCard
          title="Needs attention" icon={<AlertTriangle className="w-4 h-4" />} loading={summaryLoading}
          value={s?.attention.total} onClick={() => go("attention")} active={view === "attention"} tone={s && s.attention.total > 0 ? "warn" : undefined}
          sub={s ? `${s.attention.activeLapsed} lapsed · ${s.attention.activeNoEndDate} no end · ${s.attention.pastDueStale} stale` : ""}
          testId="kpi-attention"
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5" />Subscribers</CardTitle>
              <CardDescription>{list ? `${list.total.toLocaleString()} in this view` : "Loading…"}{isFetching && !listLoading ? " · refreshing" : ""}</CardDescription>
            </div>
            <Tabs value={view} onValueChange={v => setView(v as View)}>
              <TabsList className="flex-wrap h-auto">
                {VIEWS.map(v => (
                  <TabsTrigger key={v.value} value={v.value} data-testid={`tab-${v.value}`} className="text-xs">
                    {v.label}
                    {s && v.value === "attention" && s.attention.total > 0 && <span className="ml-1 rounded-full bg-amber-500 text-white px-1.5 text-[10px]">{s.attention.total}</span>}
                    {s && v.value === "past_due" && s.pastDue > 0 && <span className="ml-1 rounded-full bg-orange-600 text-white px-1.5 text-[10px]">{s.pastDue}</span>}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_1fr_auto] mt-3">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Name, email, username, parent email, Stripe id…" className="pl-8" value={searchInput} onChange={e => setSearchInput(e.target.value)} data-testid="input-search" />
            </div>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="select-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plans</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger data-testid="select-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                {[4, 5, 6, 7, 11].map(g => <SelectItem key={g} value={String(g)}>Grade {g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="select-provider"><SelectValue placeholder="Provider" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="apple">Apple</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
              <Checkbox checked={includeTest} onCheckedChange={v => setIncludeTest(v === true)} data-testid="checkbox-include-test" />
              Include test accounts
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {isError ? (
            <p className="text-destructive py-8 text-center">{(error as any)?.message || "Failed to load subscriptions"}</p>
          ) : listLoading && !list ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : !list?.rows.length ? (
            <div className="py-12 text-center text-muted-foreground">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              Nothing in this view.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 px-2 w-6"></th>
                    <th className="py-2 px-2 cursor-pointer select-none" onClick={() => sortToggle("name")}>Student{sortBy === "name" ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</th>
                    <th className="py-2 px-2">Plan</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Billing</th>
                    <th className="py-2 px-2 cursor-pointer select-none" onClick={() => sortToggle("endDate")}>{view === "cancelled" || view === "expired" ? "Ended" : "Renews / ends"}{sortBy === "endDate" ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</th>
                    <th className="py-2 px-2 cursor-pointer select-none" onClick={() => sortToggle("lastPayment")}>Last payment{sortBy === "lastPayment" ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</th>
                    <th className="py-2 px-2 cursor-pointer select-none" onClick={() => sortToggle("created")}>Since{sortBy === "created" ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</th>
                    <th className="py-2 px-2 text-right"></th>
                  </tr>
                </thead>
                {list.rows.map(row => {
                  const isOpen = expanded.has(row.id);
                  const canStripe = row.provider === "stripe" || (!!row.stripeCustomerId && row.provider !== "apple");
                  const endLabel = row.subscriptionEndDate ? `${relative(row.subscriptionEndDate)}` : "–";
                  return (
                    <tbody key={row.id} className="border-b last:border-0">
                      <tr className={`hover:bg-muted/40 ${row.flags.some(f => FLAG_LABELS[f]?.tone === "warn") ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`} data-testid={`row-sub-${row.id}`}>
                        <td className="py-2 px-2 align-top">
                          <button onClick={() => toggleExpanded(row.id)} className="text-muted-foreground hover:text-primary mt-1" data-testid={`button-expand-${row.id}`} aria-label="Toggle invoices">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="py-2 px-2 align-top">
                          <div className="font-medium flex items-center gap-1.5">
                            <Link href={`/admin/users?openUser=${row.id}`} className="text-primary hover:underline" data-testid={`link-student-${row.id}`} title="Open student record">
                              {row.firstName} {row.lastName}
                            </Link>
                            <Badge variant="outline" className="text-[10px] py-0 h-4">G{row.subscribedGrade ?? row.grade ?? "?"}</Badge>
                            {row.isTestAccount && <Badge variant="outline" className="text-[10px] py-0 h-4 border-blue-300 text-blue-600 gap-1"><FlaskConical className="w-3 h-3" />Test</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{row.email || row.username || "–"}{row.school ? ` · ${row.school}` : ""}</div>
                          <ParentLine row={row} />
                        </td>
                        <td className="py-2 px-2 align-top capitalize">{row.subscriptionPlan || "–"}</td>
                        <td className="py-2 px-2 align-top">
                          <StatusBadge status={row.subscriptionStatus} />
                          <div className="mt-1 space-y-0.5">
                            {row.subscriptionStatus === "past_due" && row.paymentFailedAt && (
                              <div className="text-xs text-orange-700 dark:text-orange-400">Failed {relative(row.paymentFailedAt)} · dunning day {row.dunningLastDay}</div>
                            )}
                            {row.subscriptionStatus === "paused" && row.subscriptionPausedUntil && (
                              <div className="text-xs text-muted-foreground">Resumes {formatJamaicaDate(row.subscriptionPausedUntil)}</div>
                            )}
                            {row.flags.map(f => FLAG_LABELS[f] ? (
                              <div key={f} className={`text-xs ${FLAG_LABELS[f].tone === "warn" ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}>{FLAG_LABELS[f].label}</div>
                            ) : null)}
                          </div>
                        </td>
                        <td className="py-2 px-2 align-top"><ProviderBadge row={row} /></td>
                        <td className="py-2 px-2 align-top whitespace-nowrap">
                          <div>{endLabel}</div>
                          {row.subscriptionEndDate && <div className="text-xs text-muted-foreground">{formatJamaicaDate(row.subscriptionEndDate)}</div>}
                        </td>
                        <td className="py-2 px-2 align-top whitespace-nowrap">
                          {row.lastPayment ? (
                            <div>
                              <div className="flex items-center gap-1">
                                {row.lastPayment.type === "payment_succeeded"
                                  ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                                  : <XCircle className="w-3.5 h-3.5 text-destructive" />}
                                {money(row.lastPayment.amount, row.lastPayment.currency)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatJamaicaDate(row.lastPayment.at)}
                                {row.lastPayment.type === "payment_failed" && ` · ${row.lastPayment.failureReason || "failed"} · try ${row.lastPayment.attemptCount}`}
                              </div>
                            </div>
                          ) : <span className="text-muted-foreground">–</span>}
                        </td>
                        <td className="py-2 px-2 align-top whitespace-nowrap text-xs text-muted-foreground">
                          <div>{formatJamaicaDate(row.firstPaidAt || row.createdAt)}</div>
                          {row.lastActiveAt && <div>Active {relative(row.lastActiveAt)}</div>}
                        </td>
                        <td className="py-2 px-2 align-top text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`button-actions-${row.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              {canStripe && (
                                <DropdownMenuItem onClick={() => syncMutation.mutate(row)} disabled={syncMutation.isPending} data-testid={`menu-sync-${row.id}`}>
                                  <RefreshCw className="w-4 h-4 mr-2" />Sync from Stripe
                                </DropdownMenuItem>
                              )}
                              {canStripe && (row.subscriptionStatus === "past_due" || row.flags.includes("lapsed_end_date")) && (
                                <DropdownMenuItem onClick={() => retryMutation.mutate(row)} disabled={retryMutation.isPending} data-testid={`menu-retry-${row.id}`}>
                                  <RotateCcw className="w-4 h-4 mr-2" />Retry payment
                                </DropdownMenuItem>
                              )}
                              {row.stripeSubscriptionId && (
                                <DropdownMenuItem asChild>
                                  <a href={`https://dashboard.stripe.com/subscriptions/${row.stripeSubscriptionId}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Open in Stripe</a>
                                </DropdownMenuItem>
                              )}
                              {!row.stripeSubscriptionId && row.stripeCustomerId && (
                                <DropdownMenuItem asChild>
                                  <a href={`https://dashboard.stripe.com/customers/${row.stripeCustomerId}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Open customer in Stripe</a>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/users?openUser=${row.id}`} data-testid={`menu-open-student-${row.id}`}><Users className="w-4 h-4 mr-2" />Open student record</Link>
                              </DropdownMenuItem>
                              {(row.parentEmail || row.parentPhone) && (
                                <DropdownMenuItem asChild>
                                  <Link href={`/admin/parents?search=${encodeURIComponent(row.parentEmail || row.parentPhone || "")}`} data-testid={`menu-open-parent-${row.id}`}><Users className="w-4 h-4 mr-2" />Open parent</Link>
                                </DropdownMenuItem>
                              )}
                              {row.parentAccount && (
                                <DropdownMenuItem asChild>
                                  <Link href={`/admin/users?openUser=${row.parentAccount.id}`}><Users className="w-4 h-4 mr-2" />Open parent's account</Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/comms?tab=contacts&user=${row.id}`} data-testid={`menu-comms-${row.id}`}><Mail className="w-4 h-4 mr-2" />Comms history</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link href={`/admin/metrics?tab=customers&user=${row.id}`} data-testid={`menu-metrics-${row.id}`}><BarChart3 className="w-4 h-4 mr-2" />Metrics profile</Link>
                              </DropdownMenuItem>
                              {row.parentEmail && (
                                <DropdownMenuItem asChild>
                                  <a href={`mailto:${row.parentEmail}`}><Mail className="w-4 h-4 mr-2" />Email parent</a>
                                </DropdownMenuItem>
                              )}
                              {row.parentPhone && (
                                <DropdownMenuItem asChild>
                                  <a href={`https://wa.me/${waNumber(row.parentPhone)}`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" />WhatsApp parent</a>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => { setExtendTarget(row); setExtendDays("30"); }} data-testid={`menu-extend-${row.id}`}>
                                <Plus className="w-4 h-4 mr-2" />Extend access
                              </DropdownMenuItem>
                              {canStripe && (
                                <DropdownMenuItem onClick={() => { setCouponTarget(row); setCouponCode(""); }} data-testid={`menu-coupon-${row.id}`}>
                                  <Tag className="w-4 h-4 mr-2" />Apply discount code
                                </DropdownMenuItem>
                              )}
                              {row.subscriptionStatus === "active" && (
                                <DropdownMenuItem onClick={() => pauseMutation.mutate(row.id)} disabled={pauseMutation.isPending} data-testid={`menu-pause-${row.id}`}>
                                  <Pause className="w-4 h-4 mr-2" />Pause
                                </DropdownMenuItem>
                              )}
                              {row.subscriptionStatus === "paused" && (
                                <DropdownMenuItem onClick={() => resumeMutation.mutate(row.id)} disabled={resumeMutation.isPending} data-testid={`menu-resume-${row.id}`}>
                                  <Play className="w-4 h-4 mr-2" />Resume
                                </DropdownMenuItem>
                              )}
                              {row.subscriptionStatus && !["cancelled", "canceled", "expired", "inactive"].includes(row.subscriptionStatus) && !row.subscriptionCancelAtPeriodEnd && (
                                <DropdownMenuItem onClick={() => setCancelTarget(row)} className="text-destructive focus:text-destructive" data-testid={`menu-cancel-${row.id}`}>
                                  <X className="w-4 h-4 mr-2" />Cancel subscription
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-muted/30">
                          <td></td>
                          <td colSpan={8} className="px-2">
                            <PaymentHistoryPanel userId={row.id} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
              </table>
            </div>
          )}

          {list && list.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(list.page - 1) * list.pageSize + 1}–{Math.min(list.page * list.pageSize, list.total)} of {list.total}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || isFetching} data-testid="button-prev-page"><ChevronLeft className="w-4 h-4 mr-1" />Previous</Button>
                <span className="text-sm text-muted-foreground">Page {list.page} of {list.totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(list.totalPages, p + 1))} disabled={page >= list.totalPages || isFetching} data-testid="button-next-page">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {s && s.byGrade.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Active by grade</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {s.byGrade.map(g => (
                <Badge key={String(g.grade)} variant="secondary" className="text-sm font-normal">
                  {g.grade === null ? "No grade" : `Grade ${g.grade}`}: <span className="font-semibold ml-1">{g.count}</span>
                </Badge>
              ))}
              <Badge variant="outline" className="text-sm font-normal">Stripe {s.activeStripe} · Apple {s.activeApple} · Manual {s.activeManual}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extend dialog */}
      <Dialog open={!!extendTarget} onOpenChange={o => !o && setExtendTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend access</DialogTitle>
            <DialogDescription>
              Adds days to {extendTarget?.firstName} {extendTarget?.lastName}'s end date and marks the account active. This changes EduFocal only; it does not change Stripe billing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="extend-days">Days</Label>
            <Input id="extend-days" type="number" min={1} value={extendDays} onChange={e => setExtendDays(e.target.value)} data-testid="input-extend-days" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendTarget(null)}>Cancel</Button>
            <Button
              onClick={() => extendTarget && extendMutation.mutate({ userId: extendTarget.id, days: parseInt(extendDays, 10) })}
              disabled={extendMutation.isPending || !(parseInt(extendDays, 10) > 0)}
              data-testid="button-confirm-extend"
            >
              {extendMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon dialog */}
      <Dialog open={!!couponTarget} onOpenChange={o => !o && setCouponTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply discount code</DialogTitle>
            <DialogDescription>Applies a Stripe coupon or promotion code to {couponTarget?.firstName}'s live subscription.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="coupon-code">Code</Label>
            <Input id="coupon-code" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. SUMMER30" data-testid="input-coupon" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponTarget(null)}>Cancel</Button>
            <Button onClick={() => couponTarget && couponMutation.mutate({ userId: couponTarget.id, code: couponCode.trim() })} disabled={couponMutation.isPending || !couponCode.trim()} data-testid="button-confirm-coupon">
              {couponMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={o => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {cancelTarget?.firstName} {cancelTarget?.lastName}'s subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget?.provider === "stripe"
                ? "The Stripe subscription is set to cancel at the end of the current period. Access continues until then and no refund is issued."
                : cancelTarget?.provider === "apple"
                  ? "Apple subscriptions can only be cancelled by the subscriber in their App Store settings. This only marks the EduFocal account as cancelled and ends access now."
                  : "This marks the account cancelled and ends access now."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => cancelTarget && cancelMutation.mutate(cancelTarget.id)} data-testid="button-confirm-cancel">
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reconcile confirm */}
      <AlertDialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reconcile with Stripe?</AlertDialogTitle>
            <AlertDialogDescription>
              Checks every Stripe-backed subscriber that is active, past due, paused, on trial, or set to cancel, and corrects status, plan, end date, pause and cancel flags to match Stripe. No emails or WhatsApp messages are sent. Takes about a minute.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setReconcileOpen(false); reconcileMutation.mutate(); }} data-testid="button-confirm-reconcile">Run reconcile</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reconcile result */}
      <Dialog open={!!reconcileResult} onOpenChange={o => !o && setReconcileResult(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reconcile finished</DialogTitle>
            <DialogDescription>
              Checked {reconcileResult?.checked} subscribers in {Math.round((reconcileResult?.durationMs || 0) / 1000)}s · {reconcileResult?.changed.length} corrected · {reconcileResult?.notFound.length} without a Stripe subscription · {reconcileResult?.errors.length} errors
            </DialogDescription>
          </DialogHeader>
          {reconcileResult && (
            <div className="space-y-4 text-sm">
              {reconcileResult.changed.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-1">Corrected</h4>
                  <ul className="space-y-2">
                    {reconcileResult.changed.map(c => (
                      <li key={c.userId} className="rounded border p-2">
                        <div className="font-medium">{c.name} <span className="text-muted-foreground font-normal">{c.email}</span></div>
                        <ChangeList changes={c.changes} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {reconcileResult.notFound.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-1">No Stripe subscription</h4>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {reconcileResult.notFound.map(n => <li key={n.userId}>{n.name} ({n.email}): {n.message}</li>)}
                  </ul>
                </div>
              )}
              {reconcileResult.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-1 text-destructive">Errors</h4>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {reconcileResult.errors.map(n => <li key={n.userId}>{n.name} ({n.email}): {n.error}</li>)}
                  </ul>
                </div>
              )}
              {reconcileResult.changed.length === 0 && reconcileResult.notFound.length === 0 && reconcileResult.errors.length === 0 && (
                <p className="text-muted-foreground">Everything already matched Stripe.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sync result */}
      <Dialog open={!!syncResult} onOpenChange={o => !o && setSyncResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Synced from Stripe</DialogTitle>
            <DialogDescription>
              {syncResult?.row.firstName} {syncResult?.row.lastName} · Stripe says <span className="font-medium">{syncResult?.result.stripe?.status}</span>
              {syncResult?.result.stripe?.cancelAtPeriodEnd ? ", cancelling at period end" : ""}
              {syncResult?.result.stripe?.priceAmount != null ? ` · ${money(syncResult.result.stripe.priceAmount, syncResult.result.stripe.currency || "JMD")} / ${syncResult.result.stripe.interval}` : ""}
            </DialogDescription>
          </DialogHeader>
          {syncResult && <ChangeList changes={syncResult.result.changes} />}
        </DialogContent>
      </Dialog>

      {/* Retry result */}
      <Dialog open={!!retryResult} onOpenChange={o => !o && setRetryResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{retryResult?.result.ok ? "Payment collected" : "Payment not collected"}</DialogTitle>
            <DialogDescription>
              {retryResult?.result.ok
                ? `${money(retryResult.result.amount || 0, retryResult.result.currency || "JMD")} charged for ${retryResult.row.firstName} ${retryResult.row.lastName}. The subscription row has been re-synced from Stripe.`
                : retryResult?.result.reason}
              {retryResult?.result.code ? ` (${retryResult.result.code})` : ""}
            </DialogDescription>
          </DialogHeader>
          {retryResult?.result.hostedInvoiceUrl && (
            <a href={retryResult.result.hostedInvoiceUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
              Open invoice in Stripe<ExternalLink className="w-3 h-3" />
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangeList({ changes }: { changes: SyncResult["changes"] }) {
  if (!changes.length) return <p className="text-sm text-muted-foreground">No changes.</p>;
  const pretty = (v: string | null) => v === null ? "–" : /^\d{4}-\d{2}-\d{2}T/.test(v) ? formatJamaicaDate(v) : v;
  return (
    <ul className="text-sm space-y-0.5">
      {changes.map(c => (
        <li key={c.field}><span className="text-muted-foreground">{c.field}:</span> {pretty(c.from)} → <span className="font-medium">{pretty(c.to)}</span></li>
      ))}
    </ul>
  );
}

function KpiCard({ title, icon, value, sub, foot, loading, onClick, active, tone, testId }: {
  title: string; icon: ReactNode; value: number | string | undefined; sub?: string; foot?: string;
  loading?: boolean; onClick?: () => void; active?: boolean; tone?: "warn"; testId?: string;
}) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testId}
      className={`text-left rounded-lg border p-3 transition-colors hover:bg-muted/50 ${active ? "border-primary ring-1 ring-primary" : ""} ${tone === "warn" ? "border-amber-400/70 bg-amber-50/50 dark:bg-amber-950/20" : "bg-card"}`}
    >
      <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{title}</span>{icon}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{loading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : (value ?? "–")}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</div>}
      {foot && <div className="text-xs font-medium mt-0.5 truncate">{foot}</div>}
    </button>
  );
}
